import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'terms' | 'privacy' | null;
const TERMS_CONTENT = {
  title: 'Terms of Service',
  body: [{
    heading: '1. Acceptance of Terms',
    text: 'By purchasing and accessing the Home-All-Day Reset digital playbook, you agree to be bound by these Terms of Service. If you do not agree, do not purchase or use this product.'
  }, {
    heading: '2. Digital Product License',
    text: 'Upon purchase, you are granted a single-user, non-transferable license to access and use the Home-All-Day Reset playbook for personal, non-commercial purposes only. You may not reproduce, distribute, resell, or share this content in any form.'
  }, {
    heading: '3. No Refund Policy',
    text: 'Due to the instant digital nature of this product, all sales are final. Once access is granted, no refunds will be issued. Please review the product description carefully before purchasing.'
  }, {
    heading: '4. Disclaimer of Warranties',
    text: 'This product is provided "as is" without warranties of any kind. Results described are illustrative and not guaranteed. Individual outcomes will vary based on personal circumstances and effort applied.'
  }, {
    heading: '5. Limitation of Liability',
    text: 'To the maximum extent permitted by law, the publisher shall not be liable for any indirect, incidental, or consequential damages arising from the use of this product.'
  }, {
    heading: '6. Governing Law',
    text: "These terms shall be governed by the laws of the United States. Any disputes shall be resolved in the jurisdiction of the publisher's principal place of business."
  }]
};
const PRIVACY_CONTENT = {
  title: 'Privacy Policy',
  body: [{
    heading: '1. Information We Collect',
    text: 'We collect only the information necessary to process your purchase, including your name, email address, and payment information. Payment data is processed securely by our payment processor and is never stored on our servers.'
  }, {
    heading: '2. How We Use Your Information',
    text: 'Your information is used solely to fulfill your order, deliver the digital product, and send transactional emails related to your purchase. We do not sell, rent, or share your personal information with third parties for marketing purposes.'
  }, {
    heading: '3. Email Communications',
    text: 'By purchasing, you may receive occasional emails related to your product or future offers from us. You may unsubscribe at any time by clicking the unsubscribe link in any email.'
  }, {
    heading: '4. Cookies',
    text: 'Our website may use cookies to improve your browsing experience and analyze site traffic. You may disable cookies in your browser settings, though this may affect site functionality.'
  }, {
    heading: '5. Data Security',
    text: 'We implement industry-standard security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.'
  }, {
    heading: '6. Contact Us',
    text: 'If you have questions about this Privacy Policy or how your data is handled, please contact us at support@homeallday.com.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'terms' ? TERMS_CONTENT : activeModal === 'privacy' ? PRIVACY_CONTENT : null;
  return <>
      {/* Disclaimer */}
      <div className="w-full bg-brand-bg border-t border-brand-card/50 px-4 py-10" data-id="element-43">
        <div className="max-w-3xl mx-auto" data-id="element-44">
          <p className="text-xs text-brand-text-muted/60 leading-relaxed text-center" data-id="element-45">
            <strong className="text-brand-text-muted/80 uppercase tracking-wider text-[10px]" data-id="element-46">
              Disclaimer:
            </strong>{' '}
            The Home-All-Day Reset is an educational digital product for
            informational purposes only and does not constitute professional
            marriage, psychological, or legal advice. Results are illustrative;
            individual outcomes will vary. Not a substitute for licensed therapy
            or counseling. Consult a qualified professional for serious
            concerns.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-[#0a0a0a] border-t border-brand-card px-4 py-8" data-id="element-47">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-48">
          <p className="text-xs text-brand-text-muted/50" data-id="element-49">
            &copy; {new Date().getFullYear()} Home-All-Day Reset. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-50">
            <button onClick={() => setActiveModal('terms')} className="text-xs text-brand-text-muted/60 hover:text-brand-accent transition-colors duration-200 underline underline-offset-2" data-id="element-51">
              Terms of Service
            </button>
            <button onClick={() => setActiveModal('privacy')} className="text-xs text-brand-text-muted/60 hover:text-brand-accent transition-colors duration-200 underline underline-offset-2" data-id="element-52">
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-53">
        {activeModal && content && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-54">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.25
        }} className="relative w-full max-w-2xl max-h-[80vh] bg-brand-card border border-brand-bg-alt rounded-xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-55">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-brand-bg-alt flex-shrink-0" data-id="element-56">
                <h2 className="text-xl font-serif font-bold text-white" data-id="element-57">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 text-brand-text-muted hover:text-white hover:bg-brand-bg rounded-lg transition-colors" aria-label="Close modal" data-id="element-58">
                  <X className="w-5 h-5" data-id="element-59" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto px-8 py-6 space-y-6 flex-1" data-id="element-60">
                {content.body.map((section, i) => <div key={i} data-id="element-61">
                    <h3 className="text-sm font-bold text-brand-accent uppercase tracking-wider mb-2" data-id="element-62">
                      {section.heading}
                    </h3>
                    <p className="text-sm text-brand-text-muted leading-relaxed" data-id="element-63">
                      {section.text}
                    </p>
                  </div>)}
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-5 border-t border-brand-bg-alt flex-shrink-0" data-id="element-64">
                <button onClick={() => setActiveModal(null)} className="w-full py-3 text-sm font-bold text-white bg-brand-accent hover:bg-brand-accent-hover rounded-lg transition-colors duration-200" data-id="element-65">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}